import React from "react";
import { useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * AnimatedOutlet — a MacBook-style enter-only transition around <Outlet/>.
 *
 * IMPORTANT: We deliberately avoid `AnimatePresence mode="wait"` here.
 * That combination caused admin list pages to stay stuck on the previous
 * tab's data until a full page reload — React Router switches routes
 * instantly but `mode="wait"` held the old node on screen until the
 * exit animation finished, and in some browsers the new page's
 * `useEffect` data fetch fired before the new node was actually
 * mounted in the DOM, leading to ghost state.
 *
 * This simpler pattern re-keys the wrapper on `pathname`, triggering a
 * clean mount + enter animation every time, with no exit phase. Feels
 * just as smooth (fade + tiny upward slide) but is 100% reliable.
 */
export default function AnimatedOutlet() {
  const { pathname } = useLocation();
  return (
    <motion.div
      key={pathname}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <Outlet />
    </motion.div>
  );
}
